import { BarChart3, FileSpreadsheet, RefreshCw } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import ClientesAnalitica from '../../components/pos/analitica/ClientesAnalitica'
import DetalleSeleccion from '../../components/pos/analitica/DetalleSeleccion'
import ProductosAnalitica from '../../components/pos/analitica/ProductosAnalitica'
import ResumenAnalitica from '../../components/pos/analitica/ResumenAnalitica'
import TendenciasAnalitica from '../../components/pos/analitica/TendenciasAnalitica'
import AnaliticaFiltros from '../../components/pos/analitica/AnaliticaFiltros'
import ModalMensaje from '../../components/ui/ModalMensaje'
import { useEmpresa } from '../../context/EmpresaContext'
import { useModalMensaje } from '../../hooks/useModalMensaje'
import { supabase } from '../../lib/supabase'
import {
  obtenerRangoFechaEsteAnio,
  obtenerRangoFechaMesAnterior,
  obtenerRangoFechaUltimos7Dias,
  obtenerRangoFechaUltimosNMeses,
} from '../../utils/analiticaPos'
import { construirTextoFiltrosAnalitica, exportarAnaliticaPosExcel } from '../../utils/exportarAnaliticaPos'
import {
  obtenerFechaActualPos,
  obtenerRangoFechaHoyPos,
  obtenerRangoFechaMesActualPos,
} from '../../utils/pos'

const TABS = [
  { valor: 'resumen', etiqueta: 'Resumen' },
  { valor: 'productos', etiqueta: 'Productos' },
  { valor: 'clientes', etiqueta: 'Clientes' },
  { valor: 'tendencias', etiqueta: 'Tendencias' },
]

const rangoMes = obtenerRangoFechaMesActualPos()

function construirRangosRapidos() {
  return [
    { etiqueta: 'Hoy', ...obtenerRangoFechaHoyPos() },
    { etiqueta: '7 días', ...obtenerRangoFechaUltimos7Dias() },
    { etiqueta: 'Este mes', ...obtenerRangoFechaMesActualPos() },
    { etiqueta: 'Mes anterior', ...obtenerRangoFechaMesAnterior() },
    { etiqueta: 'Últimos 3 meses', ...obtenerRangoFechaUltimosNMeses(3) },
    { etiqueta: 'Últimos 6 meses', ...obtenerRangoFechaUltimosNMeses(6) },
    { etiqueta: 'Este año', ...obtenerRangoFechaEsteAnio() },
  ]
}

function AnaliticaPos() {
  const { empresaActual } = useEmpresa()
  const { modal, abrirModal } = useModalMensaje()
  const [searchParams, setSearchParams] = useSearchParams()

  const rangosRapidos = useMemo(construirRangosRapidos, [])

  const [tab, setTab] = useState(searchParams.get('tab') || 'resumen')

  const [filtros, setFiltros] = useState({
    fechaDesde: searchParams.get('desde') || rangoMes.desde,
    fechaHasta: searchParams.get('hasta') || rangoMes.hasta,
    clienteId: '',
    productoId: '',
    estadoPago: '',
    usuarioId: '',
    tipoEnvaseId: '',
  })

  const [clientes, setClientes] = useState([])
  const [productos, setProductos] = useState([])
  const [usuarios, setUsuarios] = useState([])
  const [tiposEnvase, setTiposEnvase] = useState([])

  const [resumen, setResumen] = useState(null)
  const [filasProductos, setFilasProductos] = useState([])
  const [filasClientes, setFilasClientes] = useState([])
  const [tendenciaGeneral, setTendenciaGeneral] = useState([])

  const [cargando, setCargando] = useState(true)
  const [error, setError] = useState('')
  const [exportando, setExportando] = useState(false)

  const [productoSeleccionado, setProductoSeleccionado] = useState(null)
  const [clienteSeleccionado, setClienteSeleccionado] = useState(null)
  const [detalleTendencia, setDetalleTendencia] = useState([])
  const [detalleContrapartes, setDetalleContrapartes] = useState([])
  const [cargandoDetalle, setCargandoDetalle] = useState(false)

  const montadoRef = useRef(true)

  useEffect(() => {
    montadoRef.current = true
    return () => {
      montadoRef.current = false
    }
  }, [])

  useEffect(() => {
    setSearchParams(
      { tab, desde: filtros.fechaDesde, hasta: filtros.fechaHasta },
      { replace: true }
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, filtros.fechaDesde, filtros.fechaHasta])

  // Listas de apoyo para los selects de filtro — una sola vez por empresa.
  useEffect(() => {
    if (!empresaActual?.id) return

    const cargarListas = async () => {
      const empresaId = empresaActual.id

      const [respClientes, respProductos, respEnvases, respRelaciones] = await Promise.all([
        supabase.from('clientes').select('id, razon_social').eq('empresa_id', empresaId).eq('activo', true).order('razon_social'),
        supabase.from('productos').select('id, codigo, nombre').eq('empresa_id', empresaId).eq('activo', true).order('nombre'),
        supabase.from('tipos_envase').select('id, codigo, nombre').eq('empresa_id', empresaId).eq('activo', true).order('nombre'),
        supabase.from('usuarios_empresas').select('usuario_id').eq('empresa_id', empresaId).eq('activo', true),
      ])

      if (!montadoRef.current) return

      setClientes(respClientes.data || [])
      setProductos(respProductos.data || [])
      setTiposEnvase(respEnvases.data || [])

      const idsUsuarios = Array.from(new Set((respRelaciones.data || []).map((r) => r.usuario_id)))

      if (idsUsuarios.length > 0) {
        const respPerfiles = await supabase.from('perfiles').select('id, nombre_completo').in('id', idsUsuarios)

        if (montadoRef.current) {
          const lista = (respPerfiles.data || []).sort((a, b) =>
            (a.nombre_completo || '').localeCompare(b.nombre_completo || '')
          )
          setUsuarios(lista)
        }
      }
    }

    cargarListas()
  }, [empresaActual?.id])

  const cargarDatos = useCallback(async () => {
    if (!empresaActual?.id) return

    const params = {
      p_empresa_id: empresaActual.id,
      p_fecha_desde: filtros.fechaDesde,
      p_fecha_hasta: filtros.fechaHasta,
      p_cliente_id: filtros.clienteId || null,
      p_producto_id: filtros.productoId || null,
      p_estado_pago: filtros.estadoPago || null,
      p_usuario_id: filtros.usuarioId || null,
      p_tipo_envase_id: filtros.tipoEnvaseId || null,
    }

    try {
      setCargando(true)
      setError('')

      const [respResumen, respProductos, respClientes, respTendencia] = await Promise.all([
        supabase.rpc('obtener_analitica_pos_resumen', params),
        supabase.rpc('obtener_analitica_pos_productos', params),
        supabase.rpc('obtener_analitica_pos_clientes', params),
        supabase.rpc('obtener_analitica_pos_tendencia', params),
      ])

      if (respResumen.error) throw respResumen.error
      if (respProductos.error) throw respProductos.error
      if (respClientes.error) throw respClientes.error
      if (respTendencia.error) throw respTendencia.error

      if (!montadoRef.current) return

      setResumen(respResumen.data)
      setFilasProductos(respProductos.data || [])
      setFilasClientes(respClientes.data || [])
      setTendenciaGeneral(respTendencia.data || [])
      setProductoSeleccionado(null)
      setClienteSeleccionado(null)
    } catch (errorCarga) {
      console.error('Error al cargar Analítica POS:', errorCarga)

      if (!montadoRef.current) return

      setError(errorCarga.message || 'No fue posible cargar la analítica.')
    } finally {
      if (montadoRef.current) setCargando(false)
    }
  }, [empresaActual?.id, filtros])

  useEffect(() => {
    cargarDatos()
  }, [cargarDatos])

  // Drill-down: evolución + contrapartes del producto/cliente seleccionado.
  useEffect(() => {
    if (!productoSeleccionado && !clienteSeleccionado) {
      setDetalleTendencia([])
      setDetalleContrapartes([])
      return
    }

    const empresaId = empresaActual?.id
    if (!empresaId) return

    const paramsBase = {
      p_empresa_id: empresaId,
      p_fecha_desde: filtros.fechaDesde,
      p_fecha_hasta: filtros.fechaHasta,
      p_estado_pago: filtros.estadoPago || null,
      p_usuario_id: filtros.usuarioId || null,
      p_tipo_envase_id: filtros.tipoEnvaseId || null,
    }

    const cargarDetalle = async () => {
      try {
        setCargandoDetalle(true)

        if (productoSeleccionado) {
          const [respTendencia, respClientes] = await Promise.all([
            supabase.rpc('obtener_analitica_pos_tendencia', {
              ...paramsBase,
              p_producto_id: productoSeleccionado.producto_id,
              p_cliente_id: filtros.clienteId || null,
            }),
            supabase.rpc('obtener_analitica_pos_producto_clientes', {
              ...paramsBase,
              p_producto_id: productoSeleccionado.producto_id,
              p_cliente_id: filtros.clienteId || null,
            }),
          ])

          if (!montadoRef.current) return
          setDetalleTendencia(respTendencia.data || [])
          setDetalleContrapartes(respClientes.data || [])
        } else if (clienteSeleccionado) {
          const [respTendencia, respProductos] = await Promise.all([
            supabase.rpc('obtener_analitica_pos_tendencia', {
              ...paramsBase,
              p_cliente_id: clienteSeleccionado.cliente_id,
              p_producto_id: filtros.productoId || null,
            }),
            supabase.rpc('obtener_analitica_pos_cliente_productos', {
              ...paramsBase,
              p_cliente_id: clienteSeleccionado.cliente_id,
              p_producto_id: filtros.productoId || null,
            }),
          ])

          if (!montadoRef.current) return
          setDetalleTendencia(respTendencia.data || [])
          setDetalleContrapartes(respProductos.data || [])
        }
      } catch (errorDetalle) {
        console.error('Error al cargar detalle de Analítica POS:', errorDetalle)
      } finally {
        if (montadoRef.current) setCargandoDetalle(false)
      }
    }

    cargarDetalle()
  }, [productoSeleccionado, clienteSeleccionado, empresaActual?.id, filtros])

  const actualizarFiltro = (clave, valor) => {
    setFiltros((actual) => ({ ...actual, [clave]: valor }))
  }

  const aplicarRango = (rango) => {
    setFiltros((actual) => ({ ...actual, fechaDesde: rango.desde, fechaHasta: rango.hasta }))
  }

  const limpiarFiltrosAvanzados = () => {
    setFiltros((actual) => ({
      ...actual,
      clienteId: '',
      productoId: '',
      estadoPago: '',
      usuarioId: '',
      tipoEnvaseId: '',
    }))
  }

  const hayFiltrosAvanzadosActivos = Boolean(
    filtros.clienteId || filtros.productoId || filtros.estadoPago || filtros.usuarioId || filtros.tipoEnvaseId
  )

  const exportarExcel = async () => {
    if (exportando || !resumen) return

    try {
      setExportando(true)

      const filtrosTexto = construirTextoFiltrosAnalitica({
        fechaDesde: filtros.fechaDesde,
        fechaHasta: filtros.fechaHasta,
        clienteNombre: clientes.find((c) => c.id === filtros.clienteId)?.razon_social,
        productoNombre: productos.find((p) => p.id === filtros.productoId)?.nombre,
        estadoPago: filtros.estadoPago,
        usuarioNombre: usuarios.find((u) => u.id === filtros.usuarioId)?.nombre_completo,
        tipoEnvaseNombre: tiposEnvase.find((t) => t.id === filtros.tipoEnvaseId)?.nombre,
      })

      await exportarAnaliticaPosExcel(
        { resumen, productos: filasProductos, clientes: filasClientes, filtrosTexto },
        `analitica-pos-${obtenerFechaActualPos()}.xlsx`
      )
    } catch (errorExportar) {
      console.error('Error al exportar Analítica POS:', errorExportar)

      abrirModal({
        tipo: 'error',
        titulo: 'No se pudo exportar',
        mensaje: errorExportar.message || 'No fue posible generar el archivo Excel.',
      })
    } finally {
      setExportando(false)
    }
  }

  const renderContenidoTab = () => {
    if (tab === 'resumen') return <ResumenAnalitica datos={resumen} />

    if (tab === 'productos') {
      return (
        <div className="space-y-6">
          <ProductosAnalitica
            productos={filasProductos}
            onSeleccionarProducto={setProductoSeleccionado}
            productoSeleccionado={productoSeleccionado}
          />

          {productoSeleccionado && (
            <DetalleSeleccion
              modo="producto"
              titulo={productoSeleccionado.producto_nombre}
              cargando={cargandoDetalle}
              tendencia={detalleTendencia}
              contrapartes={detalleContrapartes}
            />
          )}
        </div>
      )
    }

    if (tab === 'clientes') {
      return (
        <div className="space-y-6">
          <ClientesAnalitica
            clientes={filasClientes}
            onSeleccionarCliente={setClienteSeleccionado}
            clienteSeleccionado={clienteSeleccionado}
          />

          {clienteSeleccionado && (
            <DetalleSeleccion
              modo="cliente"
              titulo={clienteSeleccionado.razon_social}
              cargando={cargandoDetalle}
              tendencia={detalleTendencia}
              contrapartes={detalleContrapartes}
            />
          )}
        </div>
      )
    }

    return <TendenciasAnalitica tendencia={tendenciaGeneral} />
  }

  return (
    <div className="w-full">
      <div className="mb-6 flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <p className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-emerald-600">
            <BarChart3 size={16} />
            POS / Analítica
          </p>

          <h2 className="mt-2 text-2xl font-bold text-slate-900 sm:text-3xl">Analítica POS</h2>

          <p className="mt-2 text-slate-500">
            Métricas de ventas, productos y clientes para {empresaActual?.nombre_fantasia}.
          </p>
        </div>

        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            onClick={cargarDatos}
            disabled={cargando}
            className="flex items-center justify-center gap-2 rounded-lg border border-slate-300 px-5 py-3 font-semibold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <RefreshCw size={18} className={cargando ? 'animate-spin' : ''} />
            Actualizar
          </button>

          <button
            type="button"
            onClick={exportarExcel}
            disabled={exportando || cargando || !resumen}
            className="flex items-center justify-center gap-2 rounded-lg border border-slate-300 px-5 py-3 font-semibold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <FileSpreadsheet size={18} />
            {exportando ? 'Exportando...' : 'Exportar Excel'}
          </button>
        </div>
      </div>

      <AnaliticaFiltros
        filtros={filtros}
        onCambiarFiltro={actualizarFiltro}
        onAplicarRango={aplicarRango}
        rangosRapidos={rangosRapidos}
        clientes={clientes}
        productos={productos}
        usuarios={usuarios}
        tiposEnvase={tiposEnvase}
        onLimpiar={limpiarFiltrosAvanzados}
        hayFiltrosAvanzadosActivos={hayFiltrosAvanzadosActivos}
      />

      <div className="mb-6 flex gap-2 overflow-x-auto border-b border-slate-200">
        {TABS.map((t) => (
          <button
            key={t.valor}
            type="button"
            onClick={() => setTab(t.valor)}
            className={`shrink-0 border-b-2 px-4 py-2.5 text-sm font-semibold transition ${
              tab === t.valor
                ? 'border-emerald-600 text-emerald-700'
                : 'border-transparent text-slate-500 hover:text-slate-800'
            }`}
          >
            {t.etiqueta}
          </button>
        ))}
      </div>

      {cargando ? (
        <div className="flex min-h-64 items-center justify-center">
          <div className="text-center">
            <RefreshCw size={38} className="mx-auto animate-spin text-emerald-600" />
            <p className="mt-4 font-medium text-slate-500">Cargando analítica...</p>
          </div>
        </div>
      ) : error ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-8 text-center shadow-sm">
          <p className="font-semibold text-red-700">{error}</p>
          <button
            type="button"
            onClick={cargarDatos}
            className="mt-4 rounded-lg bg-slate-800 px-5 py-2 font-semibold text-white transition hover:bg-slate-900"
          >
            Reintentar
          </button>
        </div>
      ) : (
        renderContenidoTab()
      )}

      <ModalMensaje {...modal} />
    </div>
  )
}

export default AnaliticaPos
